import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Button,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { formatRequiresDigitalSignature } from 'components/@home/@documents/SentDocuments/edit-steps/editTemplateFlow';
import DeliveryAuditReportField from 'components/@home/@documents/SentDocuments/components/DeliveryAuditReportField';
import { SendDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';
import DeliveryExpiryReminderFields from 'components/@home/@documents/SentDocuments/steps/StepMessage/DeliveryExpiryReminderFields';
import Label from 'components/@home/drawers/AppDrawer/Form/Label';
import SendMessageEmailPreview from 'components/@home/@documents/SentDocuments/steps/SendMessageEmailPreview';
import SendMessagePhonePreview from 'components/@home/@documents/SentDocuments/steps/SendMessagePhonePreview';
import { array, bool, func, number, object, oneOf, string } from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const inputSx = {
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    filter: 'invert(1)',
    opacity: 0.8,
  },
};

const StepMessage = ({
  setError,
  setNextEnabled,
  userItems,
  setUserItems,
  message,
  setMessage,
  messageSubject,
  setMessageSubject,
  signatureCardTitle = '',
  setSignatureCardTitle = () => {},
  isRoutedSend = false,
  files,
  deliveryOptions,
  setDeliveryOptions,
  signingMode = 'onePerMember',
  signingOrderRecipientCount = 0,
  sequential = false,
  setSequential = () => {},
  showDeliveryExpiryReminders,
  sendChannel = 'internal',
  emailRecipients = [],
  sendReportTo = { value: -1 },
  onSendReportToChange,
}) => {
  const { file: format } = useContext(SendDrawerContext);
  const [showEmployeeMessage, setShowEmployeeMessage] = useState(() => Boolean(message?.trim()));
  const deliveryExpiryRemindersVisible =
    showDeliveryExpiryReminders ?? formatRequiresDigitalSignature(format);
  const isInternalDigitalSign =
    sendChannel === 'internal' && formatRequiresDigitalSignature(format);

  const formatUserItems = useMemo(
    () => (format?.items || []).filter(i => i.selector === 'user'),
    [format],
  );
  const showSigningOrder =
    deliveryExpiryRemindersVisible &&
    (signingMode || 'onePerMember') === 'oneForAll' &&
    signingOrderRecipientCount >= 2;

  let messageHelperText;
  if (sendChannel === 'external') {
    messageHelperText = I18n.t('HrBot.SendStep.emailMessageFieldHint');
  } else if (isInternalDigitalSign && showEmployeeMessage) {
    messageHelperText = I18n.t('HrBot.SendStep.messageToEmployeeHint');
  }

  useEffect(() => {
    if (message?.trim()) {
      setShowEmployeeMessage(true);
    }
  }, [message]);

  useEffect(() => {
    let messageOk;
    if (sendChannel === 'external') {
      messageOk = true;
    } else if (isInternalDigitalSign) {
      messageOk = isRoutedSend || Boolean(signatureCardTitle && String(signatureCardTitle).trim());
    } else {
      messageOk = Boolean(message && String(message).trim());
    }
    setNextEnabled(messageOk && formatUserItems.every(i => userItems[i.name]));
    setError(null);
  }, [
    formatUserItems,
    isInternalDigitalSign,
    isRoutedSend,
    message,
    sendChannel,
    setError,
    setNextEnabled,
    signatureCardTitle,
    userItems,
  ]);

  return (
    <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          mt: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          justifyContent: { md: 'space-between' },
          gap: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            flex: { xs: 1, md: '1 1 56%' },
            minWidth: 0,
            maxWidth: { md: 'min(720px, 58%)' },
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {sendChannel === 'external' && (
            <>
              <Label>{I18n.t('HrBot.SendStep.messageSubjectLabel')}</Label>
              <TextField
                sx={inputSx}
                fullWidth
                value={messageSubject}
                onChange={e => setMessageSubject(e.target.value)}
                placeholder={format?.name || ''}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
            </>
          )}
          {isInternalDigitalSign &&
            (isRoutedSend ? (
              <FormHelperText sx={{ maxWidth: '100%' }}>
                {I18n.t('HrBot.SendStep.signatureCardTitleRoutedHint')}
              </FormHelperText>
            ) : (
              <>
                <Label>{I18n.t('HrBot.SendStep.signatureCardTitleLabel')}</Label>
                <TextField
                  sx={inputSx}
                  fullWidth
                  value={signatureCardTitle}
                  onChange={e => setSignatureCardTitle(e.target.value)}
                  placeholder={format?.name ? `${format.name}.pdf` : ''}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
              </>
            ))}
          {isInternalDigitalSign && !showEmployeeMessage ? (
            <Button
              variant="text"
              size="small"
              onClick={() => setShowEmployeeMessage(true)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0, minWidth: 0 }}
            >
              {I18n.t('HrBot.SendStep.addMessageLink')}
            </Button>
          ) : (
            <>
              <Label>
                {isInternalDigitalSign
                  ? I18n.t('HrBot.SendStep.messageToEmployeeLabel')
                  : I18n.t('HrBot.Message')}
              </Label>
              <TextField
                sx={inputSx}
                multiline
                {...(sendChannel === 'external' ? { minRows: 4, maxRows: 16 } : { rows: 10 })}
                value={message}
                onChange={e => setMessage(e.target.value)}
                helperText={messageHelperText}
                FormHelperTextProps={
                  sendChannel === 'external' ? { sx: { maxWidth: '100%' } } : undefined
                }
              />
            </>
          )}
          {deliveryExpiryRemindersVisible && (
            <DeliveryExpiryReminderFields
              deliveryOptions={deliveryOptions}
              setDeliveryOptions={setDeliveryOptions}
            />
          )}
          {showSigningOrder && (
            <Box
              sx={theme => ({
                mt: 2,
                pt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              })}
            >
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend">
                  {I18n.t('HrBot.SendStep.signingOrder.title')}
                </FormLabel>
                <RadioGroup
                  row
                  name="signing-order"
                  value={sequential ? 'sequential' : 'parallel'}
                  onChange={e => setSequential(e.target.value === 'sequential')}
                >
                  <FormControlLabel
                    value="parallel"
                    control={<Radio color="primary" />}
                    label={I18n.t('HrBot.SendStep.signingOrder.parallel')}
                  />
                  <FormControlLabel
                    value="sequential"
                    control={<Radio color="primary" />}
                    label={I18n.t('HrBot.SendStep.signingOrder.sequential')}
                  />
                </RadioGroup>
                <FormHelperText>
                  {sequential
                    ? I18n.t('HrBot.SendStep.signingOrder.helperSequential')
                    : I18n.t('HrBot.SendStep.signingOrder.helperParallel')}
                </FormHelperText>
              </FormControl>
            </Box>
          )}
          <Box sx={{ mt: 2, width: '100%' }}>
            <DeliveryAuditReportField sendReportTo={sendReportTo} onChange={onSendReportToChange} />
          </Box>
          {formatUserItems.map(item => (
            <React.Fragment key={item.name}>
              <Label sx={{ textTransform: 'capitalize' }}>
                {I18n.t(`HrBot.fields.${item.name}`)}
              </Label>
              <TextField
                sx={inputSx}
                rows={20}
                value={userItems[item.name]}
                onChange={e => {
                  const val = e.target?.value || '';
                  setUserItems(ui => {
                    return {
                      ...ui,
                      [item.name]: val,
                    };
                  });
                }}
              />
            </React.Fragment>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: { xs: '100%', md: 'auto' },
            flex: { md: '0 1 42%' },
            maxWidth: { md: 520 },
            alignSelf: { md: 'stretch' },
            pt: { xs: 1, md: sendChannel === 'external' ? 0.5 : 3 },
            ...(sendChannel !== 'external' && {
              pl: { xs: 2, md: 4 },
              pr: 2,
            }),
          }}
        >
          {sendChannel === 'external' ? (
            <SendMessageEmailPreview
              messageSubject={messageSubject}
              message={message}
              files={files}
              userItems={userItems}
              format={format}
              emailRecipients={emailRecipients}
            />
          ) : (
            <SendMessagePhonePreview
              message={message}
              signatureCardTitle={signatureCardTitle}
              isRoutedSend={isRoutedSend}
              files={files}
              userItems={userItems}
              format={format}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

StepMessage.propTypes = {
  setError: func.isRequired,
  setNextEnabled: func.isRequired,
  userItems: object.isRequired,
  setUserItems: func.isRequired,
  message: string.isRequired,
  messageSubject: string.isRequired,
  signatureCardTitle: string,
  setSignatureCardTitle: func,
  isRoutedSend: bool,
  files: array.isRequired,
  deliveryOptions: object.isRequired,
  setMessage: func.isRequired,
  setMessageSubject: func.isRequired,
  setDeliveryOptions: func.isRequired,
  signingMode: string,
  signingOrderRecipientCount: number,
  sequential: bool,
  setSequential: func,
  showDeliveryExpiryReminders: bool,
  sendChannel: oneOf(['internal', 'external', 'mixed']),
  emailRecipients: array,
  sendReportTo: object,
  onSendReportToChange: func.isRequired,
};

export default StepMessage;
